import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import MuiSnackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import ViewCart from 'bento-ordering/menus/components/ViewCart';
import { useIsMobile } from 'styles/hooks/mediaQueries';
import { isMobileMedia } from 'bento-ordering/utils/media';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';
import SuccessfulAddSnackbarMessage from 'bento-ordering/base/components/Snackbar/helpers/SuccessfulAddSnackbarMessage';

const SNACKBAR_MIN_WIDTH = '300px';

const MobileSnackbar = styled(MuiSnackbar, {
  shouldForwardProp: muiPropForwarding('shouldRenderTopMessage')
})(({ theme, shouldRenderTopMessage }) => ({
  '&.MuiSnackbar-root': {
    maxWidth: '100%',
    position: 'fixed',
    bottom: shouldRenderTopMessage ? '8.5rem' : '6.75rem',
    zIndex: 1,
    left: '0',
    right: '0',
    '& .MuiPaper-elevation6': {
      boxShadow: '0px 0px 7px 0 rgb(0 0 0 / 10%)'
    }
  },
  '& .MuiSnackbarContent-root': {
    position: 'fixed',
    padding: shouldRenderTopMessage ? '0 20px 8px' : '8px 20px',
    width: '100%',
    backgroundColor: theme.palette.background.default,
    color: 'red',
    flexGrow: 'initial',
    borderRadius: '0rem'
  },
  '& .MuiSnackbarContent-message': {
    padding: shouldRenderTopMessage ? '0 0 8px' : '8px 0',
    width: '100%'
  }
}));

const DesktopSnackbar = styled(MuiSnackbar, {
  shouldForwardProp: muiPropForwarding('shouldRenderTopMessage')
})(({ theme, shouldRenderTopMessage }) => ({
  '&.MuiSnackbar-root': {
    maxWidth: '100%',
    position: 'relative',
    bottom: '83.5rem',
    top: '5rem',
    right: '0.75rem',
    zIndex: theme.zIndex.appBar - 1,
    '& .MuiPaper-elevation6': {
      boxShadow:
        'rgb(0 0 0 / 8%) 0px 3px 10px 0px, rgb(0 0 0 / 1%) 0px 1px 0px 0px, rgb(0 0 0 / 1%) 0px 0px 5px 0px'
    },
    [theme.breakpoints.down('md')]: {
      bottom: '123.75rem'
    }
  },
  '&.MuiSnackbar-anchorOriginTopRight': {
    position: 'fixed',
    right: '1.75rem',
    [theme.breakpoints.up('lg')]: {
      right: 'calc(50% - 617px)'
    },
    '@media screen and (-webkit-min-device-pixel-ratio:0)': {
      '@media': {
        top: '1rem'
      }
    },
    '@media screen and (-webkit-min-device-pixel-ratio:0) and (min-resolution:.001dpcm)': {
      '@media': {
        top: '5rem'
      }
    }
  },
  '& .MuiSnackbarContent-root': {
    marginTop: shouldRenderTopMessage ? '12rem' : '8rem',
    position: 'fixed',
    padding: shouldRenderTopMessage ? '0 16px 8px' : '8px 16px',
    backgroundColor: theme.palette.background.default,
    color: 'black',
    width: '17.5rem',
    flexGrow: 'initial',
    minWidth: SNACKBAR_MIN_WIDTH,
    borderRadius: '0.5rem'
  },
  '& .MuiSnackbarContent-message': {
    padding: shouldRenderTopMessage ? '0 0 8px' : '8px 0',
    width: '100%'
  }
}));

const StyledBox = styled(Box)(({ theme }) => ({
  minWidth: SNACKBAR_MIN_WIDTH,
  marginLeft: '-16px',
  marginBottom: '16px',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '16px',
    marginLeft: '-20px',
    marginRight: '-20px'
  }
}));

const BaseSuccessfulAddSnackbar = ({
  item,
  cartQuantity,
  subtotal,
  cartItems,
  addedItemImage,
  handleSnackbarCloseOnViewCartClick,
  handleClose,
  to,
  TopMessage,
  hasInvestorDayFeature
}) => {
  // using local state here for MUI transition upon entering AND exiting screen
  // - did not seem to work properly using lastItem redux store.
  const [open, setOpen] = useState(true);

  const mobileProps = {};

  const isMobile = useIsMobile();

  if (isMobile) {
    mobileProps.autoHideDuration = 2500;
  }

  /**
   * For mobile, we delay calling handleClose until after the snackbar exit animation
   * has had time to complete. This is because handleClose dispatches
   * a redux action to reset the lastItem store, which causes a re-render and
   * prevents the exit animation from animating.
   */
  useEffect(() => {
    if (!open && isMobile) {
      const timeout = setTimeout(() => {
        handleClose();
      }, 100);

      return () => clearTimeout(timeout);
    } else if (!open && !isMobile) {
      handleClose();
    }
  }, [open, isMobile, handleClose]);

  const handleSnackbarClose = () => {
    setOpen(false);
  };

  const transitionDelay = 350;
  const image = addedItemImage ? addedItemImage.url : null;

  const getMessage = item => (
    <>
      {TopMessage && <StyledBox>{TopMessage}</StyledBox>}
      <span>
        <SuccessfulAddSnackbarMessage
          item={item}
          image={image}
          cartItems={cartItems}
        />
        {!isMobile && (
          <ViewCart
            to={to}
            quantity={cartQuantity}
            subtotal={subtotal}
            onClick={handleSnackbarCloseOnViewCartClick}
            hasInvestorDayFeature={hasInvestorDayFeature}
          />
        )}
      </span>
    </>
  );

  const commonProps = {
    ...mobileProps,
    open: open,
    onClose: handleSnackbarClose,
    message: getMessage(item),
    TransitionComponent: Slide,
    transitionDuration: {
      enter: transitionDelay,
      exit: transitionDelay
    }
  };

  return isMobileMedia() ? (
    <MobileSnackbar
      data-testid="mobile-successful-add-snackbar"
      {...commonProps}
      TransitionProps={{
        direction: 'up'
      }}
      shouldRenderTopMessage={!!TopMessage}
    />
  ) : (
    <DesktopSnackbar
      data-testid="desktop-successful-add-snackbar"
      {...commonProps}
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'top'
      }}
      TransitionProps={{
        direction: 'down'
      }}
      shouldRenderTopMessage={!!TopMessage}
    />
  );
};

BaseSuccessfulAddSnackbar.defaultProps = {
  onClose: () => {},
  cartQuantity: null,
  subtotal: null,
  cartItems: [],
  addedItemImage: {},
  to: '/cart',
  TopMessage: null,
  hasInvestorDayFeature: false
};

BaseSuccessfulAddSnackbar.propTypes = {
  item: PropTypes.string.isRequired,
  cartQuantity: PropTypes.number,
  subtotal: PropTypes.string,
  cartItems: PropTypes.array,
  addedItemImage: PropTypes.object,
  handleSnackbarCloseOnViewCartClick: PropTypes.func,
  handleClose: PropTypes.func,
  to: PropTypes.string,
  TopMessage: PropTypes.node,
  hasInvestorDayFeature: PropTypes.bool
};

export default BaseSuccessfulAddSnackbar;
